import React, { useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff, Loader2 } from 'lucide-react';
import { LevelUpFooter } from '../components/LevelUpFooter';

const API = process.env.REACT_APP_BACKEND_URL;

// Microsoft 4-square logo (inline SVG)
function MicrosoftLogo({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 21 21" style={{ flexShrink: 0 }}>
      <rect x="0"  y="0"  width="10" height="10" fill="#f25022" />
      <rect x="11" y="0"  width="10" height="10" fill="#7fba00" />
      <rect x="0"  y="11" width="10" height="10" fill="#00a4ef" />
      <rect x="11" y="11" width="10" height="10" fill="#ffb900" />
    </svg>
  );
}

// Technofocus | LevelUp branding mark (used in header, right-panel above form)
function TFLevelUpMark({ size = 'md' }) {
  const fontSize = size === 'sm' ? 14 : size === 'lg' ? 22 : 18;
  const imgH     = size === 'sm' ? 16 : size === 'lg' ? 22 : 18;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <img
        src="/technofocus-white.png"
        alt="Technofocus"
        style={{ height: imgH, filter: 'brightness(0) invert(1)', objectFit: 'contain' }}
      />
      <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: fontSize, fontWeight: 300 }}>|</span>
      <img
        src="/logo-levelup.png"
        alt="LevelUp"
        style={{ height: imgH, filter: 'brightness(0) invert(1)', objectFit: 'contain' }}
      />
    </div>
  );
}

// Header bar (replicates box.skillupwithlevelup.com header, minus "Skilling in a Box")
function TFHeader() {
  const navigate = useNavigate();
  return (
    <div
      data-testid="tf-header"
      style={{
        background: '#0e2e2e',
        padding: '0 32px',
        height: 52,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexShrink: 0,
        boxShadow: '0 1px 0 rgba(255,255,255,0.07)',
      }}
    >
      <button
        onClick={() => navigate('/')}
        aria-label="Go to home"
        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
      >
        <TFLevelUpMark size="md" />
      </button>
      <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
        <span style={{ color: 'rgba(185,220,210,0.6)', fontSize: 12 }}>Sponsored by</span>
        <MicrosoftLogo size={14} />
        <span style={{ color: 'rgba(255,255,255,0.75)', fontSize: 12, fontWeight: 500 }}>Microsoft</span>
      </div>
    </div>
  );
}

// Footer bar (replicates LevelUp brand footer — two-row version from LevelUpFooter component)
// Kept as a thin wrapper so the inline <TFFooter /> call still works in Login.jsx
function TFFooter() {
  return <LevelUpFooter />;
}

export default function Login() {
  const navigate = useNavigate();
  const [email, setEmail]           = useState('');
  const [password, setPassword]     = useState('');
  const [showPwd, setShowPwd]       = useState(false);
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState('');
  const [pendingState, setPending]  = useState(null); // null | { message }
  const [rejectedState, setRejected] = useState(null); // null | { message }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setPending(null);
    setRejected(null);
    setLoading(true);
    try {
      await axios.post(
        `${API}/api/auth/login`,
        { email: email.trim().toLowerCase(), password },
        { withCredentials: true },
      );
      navigate('/', { replace: true });
    } catch (err) {
      const detail = err.response?.data?.detail;
      const httpStatus = err.response?.status;

      if (detail && typeof detail === 'object') {
        // Support both old {code: ...} and new {error: ...} formats
        const errorCode = detail.error || detail.code;

        if (errorCode === 'access_pending') {
          setPending({ message: detail.message });
        } else if (errorCode === 'access_rejected') {
          setRejected({ message: detail.message });
        } else if (errorCode === 'access_check_unavailable') {
          setError(detail.message || 'Portal access verification is temporarily unavailable. Please try again in a few minutes.');
        } else if (errorCode === 'access_revoked') {
          setError(detail.message || 'Your access has been revoked. Contact ranjana@technofocus.co');
        } else {
          setError(detail.message || 'Login failed.');
        }
      } else if (httpStatus === 503) {
        setError('Portal access verification is temporarily unavailable. Please try again in a few minutes.');
      } else {
        setError(typeof detail === 'string' ? detail : 'Invalid email or password');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', fontFamily: '"DM Sans", sans-serif' }}>
      <TFHeader />

      <div style={{ display: 'flex', flex: 1, minHeight: 0 }}>
      {/* ── Left panel ─────────────────────────────────────────── */}
      <div
        style={{
          width: '40%', minWidth: 320, background: '#194449',
          display: 'flex', flexDirection: 'column',
          justifyContent: 'space-between', padding: '40px',
          boxSizing: 'border-box',
        }}
        data-testid="login-left-panel"
      >
        {/* Top: branding */}
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
            <span style={{ color: '#fff', fontWeight: 700, fontSize: 20, letterSpacing: '-0.3px' }}>
              Technofocus
            </span>
            <span style={{ color: '#B9DCD2', fontSize: 18, fontWeight: 300 }}>|</span>
            <span style={{ color: '#fff', fontWeight: 700, fontSize: 20, letterSpacing: '-0.3px' }}>
              Level<span style={{ color: '#D4EC8E' }}>Up</span>
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginTop: 12 }}>
            <MicrosoftLogo size={14} />
            <span style={{ color: '#B9DCD2', fontSize: 12, fontWeight: 500 }}>
              Sponsored by Microsoft
            </span>
          </div>
        </div>

        {/* Middle: tagline */}
        <div>
          <div
            style={{
              color: '#D4EC8E', fontSize: 11, fontWeight: 700,
              letterSpacing: '0.2em', textTransform: 'uppercase', marginBottom: 12,
            }}
          >
            Partner Enablement Analytics
          </div>
          <div style={{ color: '#fff', fontSize: 28, fontWeight: 700, lineHeight: 1.25, marginBottom: 12 }}>
            Data-driven insights for the LevelUp ecosystem
          </div>
          <div style={{ color: '#B9DCD2', fontSize: 14, lineHeight: 1.6 }}>
            Completions, certifications, vouchers, and partner performance — all in one place.
          </div>
        </div>

        {/* Bottom: version note */}
        <div style={{ color: 'rgba(185,220,210,0)', fontSize: 11 }}>
          &nbsp;
        </div>
      </div>

      {/* ── Right panel ────────────────────────────────────────── */}
      <div
        style={{
          flex: 1, background: '#F4F3F5',
          display: 'flex', flexDirection: 'column',
          justifyContent: 'center', alignItems: 'center',
          padding: '40px 20px', boxSizing: 'border-box',
        }}
        data-testid="login-right-panel"
      >
        <div style={{ width: '100%', maxWidth: 400 }}>
          {/* Logo above login box */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginBottom: 28 }}>
            <img
              src="/technofocus-white.png"
              alt="Technofocus"
              style={{ height: 14, filter: 'brightness(0)', objectFit: 'contain', opacity: 0.7 }}
            />
            <span style={{ color: '#b0bec5', fontSize: 14, fontWeight: 300 }}>|</span>
            <img
              src="/logo-levelup-dark.png"
              alt="LevelUp"
              style={{ height: 14, objectFit: 'contain', opacity: 0.85 }}
            />
          </div>
          <h1
            style={{
              fontFamily: '"DM Sans", sans-serif',
              fontSize: 26, fontWeight: 700,
              color: '#091F2C', marginBottom: 6,
            }}
            data-testid="login-heading"
          >
            Sign in to LevelUp Reporting
          </h1>
          <p style={{ color: '#5a6a72', fontSize: 14, marginBottom: 32 }}>
            Access requires a Microsoft or TechnoFocus account.
          </p>

          <form onSubmit={handleSubmit} noValidate>
            {/* Email */}
            <div style={{ marginBottom: 18 }}>
              <label
                htmlFor="login-email"
                style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#3a4a52', marginBottom: 6 }}
              >
                Email address
              </label>
              <input
                id="login-email"
                type="email"
                autoComplete="email"
                autoFocus
                required
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="you@microsoft.com or you@technofocus.co"
                data-testid="login-email-input"
                style={{
                  width: '100%', height: 44, padding: '0 14px',
                  border: '1.5px solid #dde3e5', borderRadius: 6,
                  fontSize: 14, fontFamily: '"DM Sans", sans-serif',
                  background: '#fff', outline: 'none', boxSizing: 'border-box',
                  transition: 'border-color 0.15s',
                }}
                onFocus={e => { e.target.style.borderColor = '#194449'; }}
                onBlur={e => { e.target.style.borderColor = '#dde3e5'; }}
              />
            </div>

            {/* Password */}
            <div style={{ marginBottom: 6 }}>
              <label
                htmlFor="login-password"
                style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#3a4a52', marginBottom: 6 }}
              >
                Password
              </label>
              <div style={{ position: 'relative' }}>
                <input
                  id="login-password"
                  type={showPwd ? 'text' : 'password'}
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="••••••••"
                  data-testid="login-password-input"
                  style={{
                    width: '100%', height: 44, padding: '0 44px 0 14px',
                    border: '1.5px solid #dde3e5', borderRadius: 6,
                    fontSize: 14, fontFamily: '"DM Sans", sans-serif',
                    background: '#fff', outline: 'none', boxSizing: 'border-box',
                    transition: 'border-color 0.15s',
                  }}
                  onFocus={e => { e.target.style.borderColor = '#194449'; }}
                  onBlur={e => { e.target.style.borderColor = '#dde3e5'; }}
                />
                <button
                  type="button"
                  onClick={() => setShowPwd(v => !v)}
                  data-testid="login-show-password-toggle"
                  style={{
                    position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)',
                    background: 'none', border: 'none', cursor: 'pointer',
                    color: '#7a8a92', padding: 0, display: 'flex',
                  }}
                  aria-label={showPwd ? 'Hide password' : 'Show password'}
                >
                  {showPwd ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            {/* Error */}
            {error && (
              <p
                data-testid="login-error-message"
                style={{
                  color: '#c0392b', fontSize: 13, marginTop: 10, marginBottom: 4,
                  background: '#fdf0ee', border: '1px solid #f5c6c0',
                  borderRadius: 5, padding: '8px 12px',
                }}
              >
                {error}
              </p>
            )}

            {/* Pending access notice */}
            {pendingState && (
              <div
                data-testid="login-pending-message"
                style={{
                  marginTop: 12, padding: '14px 16px',
                  background: '#fffbeb', border: '1px solid #f59e0b',
                  borderRadius: 8,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                  <span style={{ fontSize: 16 }}>⏳</span>
                  <span style={{ fontWeight: 700, fontSize: 14, color: '#92400e' }}>Access Request Submitted</span>
                </div>
                <p style={{ margin: 0, fontSize: 13, color: '#78350f', lineHeight: 1.5 }}>
                  {pendingState.message ||
                    "Your request to access the LevelUp Reporting Portal is under review by a Technofocus administrator. " +
                    "You'll receive confirmation once your access has been approved."}
                </p>
              </div>
            )}

            {/* Rejected access notice */}
            {rejectedState && (
              <div
                data-testid="login-rejected-message"
                style={{
                  marginTop: 12, padding: '14px 16px',
                  background: '#fef2f2', border: '1px solid #fca5a5',
                  borderRadius: 8,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                  <span style={{ fontSize: 16 }}>🚫</span>
                  <span style={{ fontWeight: 700, fontSize: 14, color: '#b91c1c' }}>Access Denied</span>
                </div>
                <p style={{ margin: 0, fontSize: 13, color: '#7f1d1d', lineHeight: 1.5 }}>
                  {rejectedState.message ||
                    "Your access to the LevelUp Reporting Portal has been denied. " +
                    "Contact ranjana@technofocus.co for assistance."}
                </p>
              </div>
            )}

            {/* Submit */}
            <button
              type="submit"
              disabled={loading || !email || !password}
              data-testid="login-submit-button"
              style={{
                marginTop: 20,
                width: '100%', height: 46, borderRadius: 6, border: 'none',
                background: '#D4EC8E', color: '#091F2C',
                fontSize: 15, fontWeight: 700,
                fontFamily: '"DM Sans", sans-serif',
                cursor: loading || !email || !password ? 'not-allowed' : 'pointer',
                opacity: loading || !email || !password ? 0.7 : 1,
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                transition: 'opacity 0.15s, background 0.15s',
              }}
              onMouseEnter={e => { if (!loading) e.currentTarget.style.background = '#c6e07a'; }}
              onMouseLeave={e => { e.currentTarget.style.background = '#D4EC8E'; }}
            >
              {loading ? (
                <>
                  <Loader2 size={18} style={{ animation: 'spin 0.7s linear infinite' }} />
                  Signing in…
                </>
              ) : 'Sign In'}
            </button>
          </form>

          <p style={{ marginTop: 24, fontSize: 12, color: '#8a9a9e', textAlign: 'center' }}>
            &nbsp;
          </p>
        </div>
      </div>

      </div>{/* end flex inner */}
      <TFFooter />
    </div>
  );
}
